import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { addToast } from '@heroui/toast';
import { IconEraser as Eraser, IconHandMove as Hand, IconLoader2 as Loader2, IconMapPin as MapPin, IconHandClick as MousePointerClick, IconPlayerPause as PauseCircle, IconPlayerPlay as PlayCircle, IconTicket as Ticket, IconX as X } from '@tabler/icons-react';
import {
    assignSeats, clearSeats, fetchSeatAssignmentsByEventWithOrderInfo, holdSeats, releaseSeats, type SeatBatchResult,
} from '@/api/seatAssignmentApi';
import SeatingCanvas, { HATCH, type SeatLook } from './SeatingCanvas';
import { buildLayout, readableTextOn, seatCategoryColor, seatStates, type SeatState } from './seating';
import { currencySymbol } from '@/utils/currency';

type TicketLike = { _id?: string; name?: string; price?: string | number; type?: string };

type Props = {
    eventId: string;
    venue: { name?: string; address?: { fullAddress?: string }; seatMap?: any };
    tickets: TicketLike[];
    onTicketsChanged?: () => void;
    readOnly?: boolean;
    currency?: string;
    holdsOnly?: boolean;
};

const formatPrice = (price: TicketLike['price'], symbol: string) => {
    const n = typeof price === 'string' ? parseFloat(price) : price ?? 0;
    return !n ? 'Free' : `${symbol}${n.toFixed(2).replace(/\.00$/, '')}`;
};

const errorMessage = (err: any) => err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.';

const EventSeatingManager: React.FC<Props> = ({ eventId, venue, tickets, onTicketsChanged, readOnly, currency, holdsOnly }) => {
    const sym = currencySymbol(currency);
    const queryClient = useQueryClient();
    const [selection, setSelection] = useState<Set<string>>(new Set());
    const [busy, setBusy] = useState<string | null>(null);
    const [holdNote, setHoldNote] = useState('');

    const { data, isLoading } = useQuery({
        queryKey: ['seat-assignments-with-order', eventId],
        queryFn: () => fetchSeatAssignmentsByEventWithOrderInfo(eventId),
        enabled: !!eventId,
    });

    const layout = useMemo(() => buildLayout(venue.seatMap), [venue.seatMap]);
    const states = useMemo(() => seatStates(data?.data ?? []), [data]);
    const seatById = useMemo(() => new Map(layout.seats.map(s => [s.id, s])), [layout]);
    const ticketList = useMemo(() => tickets.filter(t => t._id).map(t => ({ ...t, _id: t._id! })), [tickets]);
    const ticketById = useMemo(() => new Map(ticketList.map(t => [t._id, t])), [ticketList]);
    const ticketName = useCallback((id?: string) => (id && ticketById.get(id)?.name) || 'Unknown ticket', [ticketById]);

    useEffect(() => {
        setSelection(prev => {
            const valid = new Set(layout.seats.map(s => s.id));
            const next = new Set([...prev].filter(id => valid.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [layout]);

    const stateOf = useCallback((id: string): SeatState => states.get(id) ?? { kind: 'open' }, [states]);

    const seatLook = useCallback((id: string, selected: boolean): SeatLook => {
        const st = stateOf(id);
        const color = seatCategoryColor(layout, seatById.get(id));
        const base: SeatLook =
            st.kind === 'assigned' ? { fill: color, stroke: 'rgba(0,0,0,0.25)', text: readableTextOn(color) }
                : st.kind === 'hold' ? { fill: HATCH, stroke: '#B45309', text: '#78350F' }
                    : st.kind === 'sold' ? { fill: '#374151', stroke: '#111827', text: '#FFFFFF', check: 'light' }
                        : { fill: '#FFFFFF', stroke: color, strokeWidth: 2, text: '#4B5563' };
        return selected ? { ...base, stroke: '#0EA5E9', strokeWidth: 3.5 } : base;
    }, [stateOf, layout, seatById]);

    const seatTooltip = useCallback((seat: { id: string; category?: string }) => {
        const st = stateOf(seat.id);
        return (
            <>
                {seat.category && <div className="mt-0.5 text-white/70">{seat.category}</div>}
                <div className="mt-0.5 text-white/80">
                    {st.kind === 'open' && 'Not assigned'}
                    {st.kind === 'assigned' && `For sale: ${ticketName(st.ticketId)}`}
                    {st.kind === 'hold' && `On hold${st.note ? `: ${st.note}` : ''}`}
                    {st.kind === 'sold' && `Sold${st.ticketId ? ` · ${ticketName(st.ticketId)}` : ''}`}
                </div>
                {st.kind === 'sold' && st.buyer && (
                    <div className="mt-1 border-t border-white/15 pt-1 text-white/80">
                        {st.buyer.customerName && <div>{st.buyer.customerName}</div>}
                        {st.buyer.customerEmail && <div className="truncate">{st.buyer.customerEmail}</div>}
                    </div>
                )}
            </>
        );
    }, [stateOf, ticketName]);

    const stats = useMemo(() => {
        const out = { total: layout.seats.length, open: 0, assigned: 0, hold: 0, sold: 0, byTicket: new Map<string, { onSale: number; sold: number; hold: number }>() };
        layout.seats.forEach(s => {
            const st = stateOf(s.id);
            out[st.kind === 'open' ? 'open' : st.kind] += 1;
            if (st.kind !== 'open' && st.ticketId) {
                const t = out.byTicket.get(st.ticketId) ?? { onSale: 0, sold: 0, hold: 0 };
                if (st.kind === 'assigned') t.onSale++;
                else if (st.kind === 'sold') t.sold++;
                else t.hold++;
                out.byTicket.set(st.ticketId, t);
            }
        });
        return out;
    }, [layout, stateOf]);

    const categoryStats = useMemo(() => {
        const rows = layout.categories.map(c => ({ ...c, total: 0, assigned: 0 }));
        let uncategorized = 0;
        layout.seats.forEach(s => {
            const row = rows.find(r => r.name === s.category);
            if (!row) { uncategorized++; return; }
            row.total++;
            if (stateOf(s.id).kind === 'assigned') row.assigned++;
        });
        return { rows: rows.filter(r => r.total > 0), uncategorized };
    }, [layout, stateOf]);

    const selected = useMemo(() => {
        const ids = [...selection];
        const counts = { open: 0, assigned: 0, hold: 0, sold: 0 };
        ids.forEach(id => { counts[stateOf(id).kind] += 1; });
        return { ids, counts, editable: ids.filter(id => stateOf(id).kind !== 'sold') };
    }, [selection, stateOf]);

    const refresh = async () => {
        await queryClient.invalidateQueries({ queryKey: ['seat-assignments-with-order', eventId] });
        queryClient.invalidateQueries({ queryKey: ['seat-assignments', eventId] });
        onTicketsChanged?.();
    };

    const run = async (key: string, action: () => Promise<SeatBatchResult>, done: (r: SeatBatchResult) => string) => {
        setBusy(key);
        try {
            const result = await action();
            await refresh();
            setSelection(new Set());
            addToast({ title: done(result), color: 'success' });
            if (result.skipped?.length) {
                addToast({ title: `${result.skipped.length} seat${result.skipped.length === 1 ? ' was' : 's were'} skipped`, description: 'Sold seats cannot be changed.', color: 'warning' });
            }
        } catch (err) {
            addToast({ title: 'Seat update failed', description: errorMessage(err), color: 'danger' });
        } finally {
            setBusy(null);
        }
    };

    const plural = (n: number) => `${n} seat${n === 1 ? '' : 's'}`;

    const assign = (ticketId: string) =>
        run(`assign-${ticketId}`, () => assignSeats(eventId, selected.editable, ticketId), r => `${plural(r.updated)} assigned to ${ticketName(ticketId)}`);

    const hold = () =>
        run('hold', () => holdSeats(eventId, selected.editable, holdNote.trim() || undefined), r => {
            setHoldNote('');
            return `${plural(r.updated)} put on hold`;
        });

    const release = () =>
        run('release', () => releaseSeats(eventId, selected.ids.filter(id => stateOf(id).kind === 'hold')), r => `${plural(r.updated)} released`);

    const clearSelected = () =>
        run('clear', () => clearSeats(eventId, { seatUuids: selected.editable }), r => `${plural(r.updated)} cleared`);

    const clearTicket = (ticketId: string) => {
        if (!window.confirm(`Remove ${ticketName(ticketId)} from all of its unsold seats?`)) return;
        run(`clear-${ticketId}`, () => clearSeats(eventId, { ticketId }), r => `${plural(r.updated)} cleared`);
    };

    const clearAll = () => {
        if (stats.sold) return;
        if (!window.confirm('Clear every ticket assignment and hold on this seat map?')) return;
        run('clear-all', () => clearSeats(eventId), r => `${plural(r.updated)} cleared`);
    };

    const selectWhere = (pred: (st: SeatState) => boolean) =>
        setSelection(new Set(layout.seats.filter(s => pred(stateOf(s.id))).map(s => s.id)));

    if (!layout.seats.length) {
        return (
            <div className="rounded bg-white dark:bg-nightElevated px-4 py-12 text-center text-sm text-[#666] dark:text-nightMuted">
                This venue's seat map has no seats yet.
            </div>
        );
    }

    const legend = [
        { label: 'Not assigned', count: stats.open, swatch: 'bg-white border border-gray-300', onSelect: () => selectWhere(st => st.kind === 'open') },
        { label: 'For sale', count: stats.assigned, swatch: 'bg-gray-400', onSelect: () => selectWhere(st => st.kind === 'assigned') },
        { label: 'On hold', count: stats.hold, swatch: 'bg-amber-200 border border-amber-600', onSelect: () => selectWhere(st => st.kind === 'hold') },
        { label: 'Sold', count: stats.sold, swatch: 'bg-gray-700', onSelect: undefined },
    ];

    const btn = 'inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded text-xs font-semibold transition-colors disabled:opacity-40 disabled:pointer-events-none';

    return (
        <section className="rounded bg-white dark:bg-nightElevated overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-nightBorder">
                <div className="min-w-0">
                    <h3 className="text-lg md:text-xl font-bold text-[#222] dark:text-nightTextSecondary">{holdsOnly ? 'Holds' : 'Seating'}</h3>
                    {venue.name && (
                        <div className="flex items-center gap-1.5 text-xs text-[#666] dark:text-nightMuted min-w-0">
                            <MapPin className="w-3.5 h-3.5 text-primary40 dark:text-nightAccent flex-shrink-0" />
                            <span className="truncate">{venue.name}{venue.address?.fullAddress ? ` · ${venue.address.fullAddress}` : ''}</span>
                        </div>
                    )}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                    {legend.filter(l => !holdsOnly || l.label === 'On hold' || l.label === 'Sold').map(l => (
                        <button
                            key={l.label}
                            type="button"
                            disabled={!l.onSelect || !l.count || readOnly}
                            onClick={l.onSelect}
                            title={l.onSelect ? `Select all ${l.label.toLowerCase()} seats` : undefined}
                            className="flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-grey99 dark:bg-nightSurface text-xs text-[#444] dark:text-nightTextSecondary enabled:hover:bg-gray-200 dark:enabled:hover:bg-white/10 disabled:cursor-default"
                        >
                            <span className={`h-3 w-3 rounded-full ${l.swatch}`} />
                            {l.label}
                            <span className="tabular-nums font-semibold">{l.count}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex flex-col lg:flex-row">
                <div className="relative h-[60vh] min-h-[360px] max-h-[520px] w-full lg:h-[620px] lg:max-h-none lg:w-auto lg:flex-1 min-w-0">
                    {isLoading && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-black/40">
                            <Loader2 className="h-6 w-6 animate-spin text-primary40 dark:text-nightAccent" />
                        </div>
                    )}
                    <SeatingCanvas
                        layout={layout}
                        look={seatLook}
                        tooltip={seatTooltip}
                        selection={selection}
                        onSelectionChange={setSelection}
                        readOnly={readOnly}
                    />
                </div>

                <aside className="w-full lg:w-80 flex-shrink-0 border-t lg:border-t-0 lg:border-l border-gray-100 dark:border-nightBorder flex flex-col lg:max-h-[620px]">
                    {selected.ids.length > 0 && !readOnly ? (
                        <div className="flex-1 overflow-y-auto p-4 space-y-5">
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <div className="text-sm font-semibold text-[#222] dark:text-nightTextSecondary">{plural(selected.ids.length)} selected</div>
                                    <div className="text-xs text-[#888] dark:text-nightMuted">
                                        {[
                                            selected.counts.open && `${selected.counts.open} unassigned`,
                                            selected.counts.assigned && `${selected.counts.assigned} for sale`,
                                            selected.counts.hold && `${selected.counts.hold} on hold`,
                                            selected.counts.sold && `${selected.counts.sold} sold (locked)`,
                                        ].filter(Boolean).join(' · ')}
                                    </div>
                                </div>
                                <button type="button" onClick={() => setSelection(new Set())} aria-label="Clear selection" className="h-7 w-7 flex items-center justify-center rounded text-[#888] hover:bg-gray-100 dark:hover:bg-white/10">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>

                            {!holdsOnly && (
                            <div>
                                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-[#888] dark:text-nightMuted">Sell as</div>
                                {ticketList.length ? (
                                    <div className="space-y-1.5">
                                        {ticketList.map(t => (
                                            <button
                                                key={t._id}
                                                type="button"
                                                disabled={!selected.editable.length || !!busy}
                                                onClick={() => assign(t._id)}
                                                className="w-full flex items-center gap-2.5 rounded border border-gray-200 dark:border-nightBorder px-3 py-2 text-left hover:border-primary40 dark:hover:border-nightAccent transition-colors disabled:opacity-40"
                                            >
                                                <Ticket className="h-3.5 w-3.5 flex-shrink-0 text-[#888] dark:text-nightMuted" />
                                                <span className="flex-1 min-w-0 truncate text-sm text-[#222] dark:text-nightTextSecondary">{t.name || 'Untitled ticket'}</span>
                                                <span className="text-xs text-[#888] dark:text-nightMuted">{formatPrice(t.price, sym)}</span>
                                                {busy === `assign-${t._id}` && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs text-[#888] dark:text-nightMuted">Save a ticket above to start assigning seats.</p>
                                )}
                            </div>
                            )}

                            <div>
                                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-[#888] dark:text-nightMuted">Hold</div>
                                <input
                                    value={holdNote}
                                    onChange={e => setHoldNote(e.target.value)}
                                    maxLength={200}
                                    placeholder="Reason (optional), e.g. Sponsors, Production"
                                    className="w-full h-11 px-4 mb-2 rounded-2xl bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10 text-sm text-grey10 dark:text-nightTextSecondary placeholder:text-grey60 outline-none focus:ring-2 focus:ring-primary40 dark:focus:ring-nightAccent"
                                />
                                <div className="grid grid-cols-2 gap-2">
                                    <button type="button" onClick={hold} disabled={!selected.editable.length || !!busy} className={`${btn} bg-amber-100 text-amber-900 hover:bg-amber-200 dark:bg-amber-500/15 dark:text-amber-300`}>
                                        {busy === 'hold' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PauseCircle className="h-3.5 w-3.5" />} Put on hold
                                    </button>
                                    <button type="button" onClick={release} disabled={!selected.counts.hold || !!busy} className={`${btn} bg-grey99 text-[#222] hover:bg-gray-200 dark:bg-nightSurface dark:text-nightTextSecondary`}>
                                        {busy === 'release' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />} Release
                                    </button>
                                </div>
                                <p className="mt-1.5 text-[11px] text-[#888] dark:text-nightMuted">Held seats can't be bought. Releasing returns them to their ticket.</p>
                            </div>

                            {!holdsOnly && (
                            <button
                                type="button"
                                onClick={clearSelected}
                                disabled={!(selected.counts.assigned + selected.counts.hold) || !!busy}
                                className={`${btn} w-full border border-error80 text-error40 hover:bg-error95 dark:border-error30 dark:hover:bg-error10`}
                            >
                                {busy === 'clear' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eraser className="h-3.5 w-3.5" />} Remove assignment
                            </button>
                            )}
                        </div>
                    ) : (
                        <div className="flex-1 overflow-y-auto p-4 space-y-5">
                            {!readOnly && (
                                <div className="rounded bg-grey99 dark:bg-nightSurface p-3 text-xs text-[#555] dark:text-nightMuted space-y-1.5">
                                    <div className="flex items-center gap-2 font-semibold text-[#222] dark:text-nightTextSecondary"><MousePointerClick className="h-3.5 w-3.5" /> Select seats to assign</div>
                                    <div className="hidden [@media(pointer:fine)]:block">Click seats or a row label. Drag to box-select. Hold Shift to add, Alt to remove.</div>
                                    <div className="hidden [@media(pointer:fine)]:flex items-center gap-1.5"><Hand className="h-3 w-3" /> Scroll or right-drag to move. Ctrl + scroll to zoom.</div>
                                    <div className="[@media(pointer:fine)]:hidden">Tap seats or a row label to select. Double-tap a seat for its whole row. Drag to move, pinch to zoom.</div>
                                </div>
                            )}

                            {!holdsOnly && categoryStats.rows.length > 0 && (
                                <div>
                                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-[#888] dark:text-nightMuted">Seat tiers</div>
                                    <ul className="space-y-1.5">
                                        {categoryStats.rows.map(c => (
                                            <li key={c.name} className="flex items-center gap-2.5 rounded border border-gray-100 dark:border-nightBorder px-3 py-2">
                                                <span className="h-3.5 w-3.5 rounded-full flex-shrink-0 border border-black/10" style={{ background: c.color }} />
                                                <span className="flex-1 min-w-0 truncate text-sm text-[#222] dark:text-nightTextSecondary">{c.name}</span>
                                                <span className="text-[11px] text-[#888] dark:text-nightMuted">{c.assigned}/{c.total} for sale</span>
                                                {!readOnly && (
                                                    <button type="button" className="text-[11px] text-primary40 dark:text-nightAccent hover:underline" onClick={() => setSelection(new Set(layout.seats.filter(s => s.category === c.name).map(s => s.id)))}>Select</button>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                    {categoryStats.uncategorized > 0 && <p className="mt-1.5 text-[11px] text-[#888] dark:text-nightMuted">{categoryStats.uncategorized} seats have no tier.</p>}
                                    <p className="mt-1.5 text-[11px] text-[#888] dark:text-nightMuted">Seat colors come from the venue's seat map tiers. Unassigned seats show an outline, for sale seats are filled.</p>
                                </div>
                            )}

                            {!holdsOnly && (
                            <div>
                                <div className="mb-2 flex items-center justify-between">
                                    <span className="text-[11px] font-semibold uppercase tracking-wider text-[#888] dark:text-nightMuted">Tickets</span>
                                </div>
                                {ticketList.length ? (
                                    <ul className="space-y-1.5">
                                        {ticketList.map(t => {
                                            const s = stats.byTicket.get(t._id) ?? { onSale: 0, sold: 0, hold: 0 };
                                            const total = s.onSale + s.sold + s.hold;
                                            return (
                                                <li key={t._id} className="group rounded border border-gray-100 dark:border-nightBorder px-3 py-2">
                                                    <div className="flex items-center gap-2.5">
                                                        <Ticket className="h-3.5 w-3.5 flex-shrink-0 text-[#888] dark:text-nightMuted" />
                                                        <span className="flex-1 min-w-0 truncate text-sm text-[#222] dark:text-nightTextSecondary">{t.name || 'Untitled ticket'}</span>
                                                        <span className="text-xs text-[#888] dark:text-nightMuted">{formatPrice(t.price, sym)}</span>
                                                    </div>
                                                    <div className="mt-1 flex items-center justify-between gap-2 pl-6 text-[11px] text-[#888] dark:text-nightMuted">
                                                        <span>{total ? `${s.onSale} for sale · ${s.sold} sold${s.hold ? ` · ${s.hold} held` : ''}` : 'No seats assigned'}</span>
                                                        {!readOnly && total > 0 && (
                                                            <span className="flex items-center gap-2">
                                                                <button type="button" className="text-primary40 dark:text-nightAccent hover:underline" onClick={() => selectWhere(st => st.kind !== 'open' && st.kind !== 'sold' && st.ticketId === t._id)}>Select</button>
                                                                {s.onSale + s.hold > 0 && (
                                                                    <button type="button" disabled={!!busy} className="text-error40 hover:underline disabled:opacity-40" onClick={() => clearTicket(t._id)}>
                                                                        {busy === `clear-${t._id}` ? 'Clearing...' : 'Clear'}
                                                                    </button>
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : (
                                    <p className="flex items-center gap-2 text-xs text-[#888] dark:text-nightMuted"><Ticket className="h-3.5 w-3.5" /> No tickets yet.</p>
                                )}
                            </div>
                            )}

                            {layout.zones.length > 1 && !readOnly && (
                                <div>
                                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-[#888] dark:text-nightMuted">Select a section</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {layout.zones.map(z => (
                                            <button
                                                key={z.name}
                                                type="button"
                                                onClick={() => setSelection(new Set(z.seatIds))}
                                                className="h-7 px-2.5 rounded-full border border-gray-200 dark:border-nightBorder text-xs text-[#444] dark:text-nightTextSecondary hover:border-primary40 dark:hover:border-nightAccent"
                                            >
                                                {z.name} <span className="text-[#999]">{z.seatIds.length}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {!readOnly && !holdsOnly && (
                        <div className="border-t border-gray-100 dark:border-nightBorder p-4">
                            <button
                                type="button"
                                onClick={clearAll}
                                disabled={!(stats.assigned + stats.hold) || stats.sold > 0 || !!busy}
                                title={stats.sold ? 'Unavailable because some seats already have orders' : undefined}
                                className={`${btn} w-full text-error40 hover:bg-error95 dark:hover:bg-error10`}
                            >
                                {busy === 'clear-all' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eraser className="h-3.5 w-3.5" />} Clear all assignments
                            </button>
                            {stats.sold > 0 && (
                                <p className="mt-1.5 text-center text-[11px] text-[#888] dark:text-nightMuted">
                                    Unavailable: {stats.sold} seat{stats.sold === 1 ? ' has' : 's have'} orders. Clear seats or tickets individually instead.
                                </p>
                            )}
                        </div>
                    )}
                </aside>
            </div>
        </section>
    );
};

export default EventSeatingManager;
